import { useEffect, useRef, useState } from 'react';
import type { LogEntry } from '../../types/interfaces';
import { useTranslation } from '../../i18n/useTranslation';
import styles from './CombatLog.module.css';

interface CombatLogProps {
  entries: LogEntry[];
}

const ICONS: Record<string, string> = {
  action:   '⚔',
  damage:   '💥',
  heal:     '💚',
  movement: '👣',
  system:   '⚙',
};

export function CombatLog({ entries }: CombatLogProps) {
  const { t } = useTranslation();
  const bottomRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(window.innerWidth >= 1024);

  // Escuta o redimensionamento da janela para esconder/mostrar de forma adaptativa
  // mas apenas quando cruza o breakpoint, para não atrapalhar o usuário.
  useEffect(() => {
    let wasMobile = window.innerWidth < 1024;
    const handleResize = () => {
      const isMobile = window.innerWidth < 1024;
      if (isMobile !== wasMobile) {
        setIsOpen(!isMobile);
        wasMobile = isMobile;
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Auto-scroll to bottom when new entries arrive
  useEffect(() => {
    if (isOpen) {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [entries.length, isOpen]);

  return (
    <>
      {!isOpen && (
        <button 
          className={styles.logToggleBtn} 
          onClick={() => setIsOpen(true)}
          title={t('log.title')}
        >
          📄
        </button>
      )}

      {isOpen && (
        <div className={styles.logContainer}>
          <div className={styles.logHeader}>
            <span>{t('log.title')}</span>
            <button className={styles.closeBtn} onClick={() => setIsOpen(false)}>&times;</button>
          </div>
          <div className={styles.logEntries}>
            {entries.map((entry, i) => (
              <div
                key={i}
                className={styles.entry}
                data-tipo={entry.type}
              >
                <span className={styles.icon}>{ICONS[entry.type] ?? '•'}</span>
                <span className={styles.turnTag}>T{entry.turn}</span>
                <span className={styles.message}>{entry.message}</span>
              </div>
            ))}
            <div ref={bottomRef} />
          </div>
        </div>
      )}
    </>
  );
}
